import { useState } from "react";
import { useTranslation } from "react-i18next";

/**
 * apps/web :: components/onboarding :: CdlInterestStep
 *
 * Step 5. Single boolean toggle — "am I interested in CDL content?". Always
 * valid; default false. The wizard's review step shows the final choice
 * before PUT /api/profile.
 */
export interface CdlInterestStepProps {
  value: boolean;
  onNext: (cdlInterest: boolean) => void;
  onBack: () => void;
}

export default function CdlInterestStep({
  value,
  onNext,
  onBack,
}: CdlInterestStepProps) {
  const { t } = useTranslation();
  const [draft, setDraft] = useState<boolean>(value);

  return (
    <div className="space-y-md">
      <header>
        <h2 className="font-display text-xl font-semibold text-brand-primary">
          {t("onboarding.cdlInterest.title")}
        </h2>
        <p className="mt-xs text-sm text-neutral-700">
          {t("onboarding.cdlInterest.body")}
        </p>
      </header>

      <section className="rounded-md border border-brand-primary/10 bg-white p-md">
        <div className="flex items-start justify-between gap-md">
          <div className="flex-1">
            <label
              htmlFor="cdl-interest-toggle"
              className="text-sm font-semibold text-brand-primary"
            >
              {t("onboarding.cdlInterest.toggleLabel")}
            </label>
            <p className="mt-xs text-xs text-neutral-600">
              {draft
                ? t("onboarding.cdlInterest.hintOn")
                : t("onboarding.cdlInterest.hintOff")}
            </p>
          </div>
          <div className="shrink-0">
            <label className="relative inline-flex cursor-pointer items-center">
              <input
                id="cdl-interest-toggle"
                type="checkbox"
                role="switch"
                checked={draft}
                onChange={(ev) => setDraft(ev.target.checked)}
                data-testid="cdl-interest-toggle"
                className="peer sr-only"
              />
              <span
                aria-hidden="true"
                className="h-6 w-11 rounded-full bg-neutral-300 transition-colors after:absolute after:top-0.5 after:left-0.5 after:h-5 after:w-5 after:rounded-full after:bg-white after:shadow after:transition-transform peer-checked:bg-brand-primary peer-checked:after:translate-x-5"
              />
            </label>
          </div>
        </div>
      </section>

      {/*
        ROADS-515: many newcomers don't know what a CDL is. The toggle
        alone reads as opaque jargon. This collapsible panel lets the
        user open a short explainer before deciding. Default-collapsed
        so it doesn't push the Next button below the fold.
      */}
      <details
        className="group rounded-md border border-brand-primary/10 bg-brand-surface/40 px-md py-sm"
        data-testid="cdl-explainer-panel"
      >
        <summary
          className="flex cursor-pointer list-none items-center justify-between gap-sm text-sm font-medium text-brand-primary marker:hidden focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-primary"
          data-testid="cdl-explainer-toggle"
        >
          <span className="inline-flex items-center gap-xs">
            <TruckIcon />
            {t("onboarding.cdlInterest.whatIsCdl.toggle")}
          </span>
          <span
            aria-hidden="true"
            className="text-lg leading-none transition-transform group-open:rotate-180"
          >
            ⌃
          </span>
        </summary>
        {/*
          No nested <h3> inside the panel — the outer step heading
          already names the topic (`cdlInterest.title`). Adding a second
          "Commercial Driver's License" heading collides with the
          existing ROADS-505 test that selects the step by heading
          regex, and the explainer summary already labels the panel.
        */}
        <div className="mt-sm space-y-sm text-sm text-neutral-700">
          <p>{t("onboarding.cdlInterest.whatIsCdl.bodyP1")}</p>
          <p>{t("onboarding.cdlInterest.whatIsCdl.bodyP2")}</p>
          <p>{t("onboarding.cdlInterest.whatIsCdl.bodyP3")}</p>
          <p className="text-xs italic text-neutral-600">
            {t("onboarding.cdlInterest.whatIsCdl.disclaimer")}
          </p>
        </div>
      </details>

      <div className="flex items-center justify-between gap-sm pt-md">
        <button
          type="button"
          data-testid="onboarding-back"
          onClick={onBack}
          className="inline-flex items-center justify-center rounded-md border border-brand-primary/20 bg-white px-md py-sm text-sm font-medium text-brand-primary transition-colors hover:border-brand-primary/40 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-primary"
        >
          {t("onboarding.back")}
        </button>
        <button
          type="button"
          data-testid="onboarding-next"
          onClick={() => onNext(draft)}
          className="inline-flex items-center justify-center rounded-md bg-brand-primary px-md py-sm text-sm font-semibold text-white transition-colors hover:bg-brand-primary/90 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-primary"
        >
          {t("onboarding.next")}
        </button>
      </div>
    </div>
  );
}

/**
 * Small inline silhouette of a US semi-truck — used as a visual anchor
 * for the "What is a CDL?" explainer (ROADS-515). Kept inline (vs an
 * external asset) so it ships with the bundle and renders in the user's
 * current text color via `currentColor`.
 *
 * The ticket scope mentioned a royalty-free photograph; that's deferred
 * to an operator follow-up (image sourcing + licensing). The icon below
 * carries the visual cue without blocking the explainer copy from
 * shipping.
 */
function TruckIcon(): JSX.Element {
  return (
    <svg
      width="20"
      height="14"
      viewBox="0 0 28 16"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      data-testid="cdl-explainer-truck-icon"
    >
      {/* Cab */}
      <path d="M16 4h5l4 4v3" />
      {/* Trailer */}
      <rect x="1" y="2" width="15" height="9" rx="1" />
      {/* Chassis */}
      <path d="M1 11h24" />
      {/* Wheels */}
      <circle cx="6" cy="13" r="1.5" />
      <circle cx="20" cy="13" r="1.5" />
    </svg>
  );
}
